import { z } from "zod"

import { tenantIdSchema } from "./identifiers.js"
import { platformCommandEnvelopeV1Schema } from "./platform-command-v1.js"

const timestampSchema = z.iso.datetime({ offset: true })
const codeSchema = z.string().regex(/^[a-z][a-z0-9_.-]{2,63}$/)
const safeTextSchema = z.string().trim().min(1).max(4_000)

export const platformSupportCaseStateV1Schema = z.enum([
  "open",
  "investigating",
  "waiting_customer",
  "waiting_internal",
  "resolved",
  "closed",
])

export const platformSupportCasePriorityV1Schema = z.enum([
  "low",
  "normal",
  "high",
  "urgent",
])

export const platformSupportCustomerMessageV1Schema = z.object({
  messageId: z.uuid(),
  audience: z.literal("customer"),
  authorKind: z.enum(["platform_operator", "tenant_contact"]),
  authorReference: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{2,127}$/),
  body: safeTextSchema,
  redactionConfirmed: z.literal(true),
  createdAt: timestampSchema,
}).strict()

export const platformSupportInternalNoteV1Schema = z.object({
  noteId: z.uuid(),
  audience: z.literal("platform_internal"),
  authorPlatformOperatorId: z.uuid(),
  body: safeTextSchema,
  redactionConfirmed: z.literal(true),
  createdAt: timestampSchema,
}).strict()

export const platformSupportSlaV1Schema = z.object({
  responseDueAt: timestampSchema,
  resolutionDueAt: timestampSchema,
  pausedAt: timestampSchema.nullable(),
  pauseReasonCode: codeSchema.nullable(),
  pausedTotalSeconds: z.number().int().nonnegative().safe(),
}).strict().superRefine((sla, context) => {
  if ((sla.pausedAt === null) !== (sla.pauseReasonCode === null)) {
    context.addIssue({
      code: "custom",
      path: ["pausedAt"],
      message: "SLA pause time and documented reason must be present together.",
    })
  }
})

export const platformSupportCaseSnapshotV1Schema = z.object({
  contractVersion: z.literal(1),
  platformSupportCaseId: z.uuid(),
  referenceCode: z.string().regex(/^PSC-[0-9]{6}$/),
  title: z.string().trim().min(3).max(160),
  categoryCode: codeSchema,
  priority: platformSupportCasePriorityV1Schema,
  state: platformSupportCaseStateV1Schema,
  referencedTenantIds: z.array(tenantIdSchema).max(8),
  assignedPlatformOperatorId: z.uuid().nullable(),
  customerMessages: z.array(platformSupportCustomerMessageV1Schema).max(500),
  internalNotes: z.array(platformSupportInternalNoteV1Schema).max(500),
  sla: platformSupportSlaV1Schema,
  resolutionCode: codeSchema.nullable(),
  revision: z.number().int().positive().safe(),
  openedAt: timestampSchema,
  updatedAt: timestampSchema,
  safeMetadataOnly: z.literal(true),
}).strict().superRefine((supportCase, context) => {
  if (new Set(supportCase.referencedTenantIds).size !== supportCase.referencedTenantIds.length) {
    context.addIssue({
      code: "custom",
      path: ["referencedTenantIds"],
      message: "Referenced Tenants must be unique.",
    })
  }
  const waiting = supportCase.state === "waiting_customer"
    || supportCase.state === "waiting_internal"
  if (waiting !== (supportCase.sla.pausedAt !== null)) {
    context.addIssue({
      code: "custom",
      path: ["sla"],
      message: "SLA may pause only in a documented waiting state.",
    })
  }
  if ((supportCase.state === "resolved" || supportCase.state === "closed")
      !== (supportCase.resolutionCode !== null)) {
    context.addIssue({
      code: "custom",
      path: ["resolutionCode"],
      message: "Resolution evidence must agree with the case state.",
    })
  }
})

const openActionSchema = z.object({
  action: z.literal("open"),
  platformSupportCaseId: z.uuid(),
  referenceCode: z.string().regex(/^PSC-[0-9]{6}$/),
  title: z.string().trim().min(3).max(160),
  categoryCode: codeSchema,
  priority: platformSupportCasePriorityV1Schema,
  referencedTenantIds: z.array(tenantIdSchema).max(8),
  responseDueAt: timestampSchema,
  resolutionDueAt: timestampSchema,
}).strict()

const caseActionSchema = <T extends "begin_investigation" | "close">(
  action: T,
) => z.object({
  action: z.literal(action),
  platformSupportCaseId: z.uuid(),
}).strict()

const customerMessageActionSchema = z.object({
  action: z.literal("add_customer_message"),
  platformSupportCaseId: z.uuid(),
  message: platformSupportCustomerMessageV1Schema.omit({ createdAt: true }),
}).strict()

const internalNoteActionSchema = z.object({
  action: z.literal("add_internal_note"),
  platformSupportCaseId: z.uuid(),
  note: platformSupportInternalNoteV1Schema.omit({
    authorPlatformOperatorId: true,
    createdAt: true,
  }),
}).strict()

const waitActionSchema = z.object({
  action: z.literal("enter_waiting"),
  platformSupportCaseId: z.uuid(),
  waitingFor: z.enum(["customer", "internal"]),
  reasonCode: codeSchema,
}).strict()

const resumeActionSchema = z.object({
  action: z.literal("resume"),
  platformSupportCaseId: z.uuid(),
}).strict()

const resolveActionSchema = z.object({
  action: z.literal("resolve"),
  platformSupportCaseId: z.uuid(),
  resolutionCode: codeSchema,
}).strict()

export const managePlatformSupportCaseActionV1Schema = z.discriminatedUnion("action", [
  openActionSchema,
  caseActionSchema("begin_investigation"),
  customerMessageActionSchema,
  internalNoteActionSchema,
  waitActionSchema,
  resumeActionSchema,
  resolveActionSchema,
  caseActionSchema("close"),
])

export const managePlatformSupportCaseV1Schema = z.object({
  envelope: platformCommandEnvelopeV1Schema.safeExtend({
    commandType: z.literal("ManagePlatformSupportCase"),
  }),
  request: managePlatformSupportCaseActionV1Schema,
}).strict().superRefine((command, context) => {
  if (
    command.envelope.idempotencyNamespace !== "platform.support_case.manage"
    || command.envelope.target.scope !== "platform"
    || command.envelope.target.targetTenantId !== null
    || command.envelope.target.targetType !== "platform_support_case"
    || command.envelope.target.targetId !== command.request.platformSupportCaseId
    || command.envelope.requiredApprovalIds.length !== 0
  ) {
    context.addIssue({
      code: "custom",
      path: ["envelope"],
      message: "Platform Support Case command must bind one platform case without Tenant authority.",
    })
  }
})

export const platformSupportCasePageV1Schema = z.object({
  responseVersion: z.literal(1),
  generatedAt: timestampSchema,
  items: z.array(platformSupportCaseSnapshotV1Schema).max(100),
  nextCursor: z.uuid().nullable(),
  partial: z.boolean(),
  safeMetadataOnly: z.literal(true),
}).strict()

export type PlatformSupportCaseSnapshotV1 = z.infer<typeof platformSupportCaseSnapshotV1Schema>
export type PlatformSupportCasePageV1 = z.infer<typeof platformSupportCasePageV1Schema>
export type ManagePlatformSupportCaseV1 = z.infer<typeof managePlatformSupportCaseV1Schema>
